import { motion } from "framer-motion";
import { MapPin, Building2, UtensilsCrossed, Plus, Edit2, Search, X, Tag, Star, BarChart3, ThumbsUp, MessageSquare, CheckSquare, Square, RefreshCw, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useMemo, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { viewportClamp } from "../lib/floating";
import {
  useAdminNeighborhoods,
  useAdminRestaurants,
  useCreateCity,
  useCreateNeighborhood,
  useCreateRestaurant,
  useUpdateCity,
  useUpdateNeighborhood,
  useUpdateRestaurant,
  useGoogleMapsSearch,
  useStaticCities,
  useRestaurantStats,
} from "../api/useAdminNeighborhoods";
import { useAdminFeedback } from "../api/useAdminFeedback";
import { AdminAPI } from "@shared/types/api";
import OverallVenuesMap, { type MapBounds } from "@/components/OverallVenuesMap";
import { Loader } from '@/components/Loader';
import { FilterDropdown } from "../components/FilterDropdown";

// Restaurant Rating Histogram Component
function RatingHistogram({ distribution }: { distribution: AdminAPI.RestaurantStats['ratingDistribution'] }) {
  const total = distribution.perfect + distribution.good + distribution.okay + distribution.notGreat + distribution.poor;
  if (total === 0) return null;

  const bars = [
    { label: 'Perfect', value: distribution.perfect, color: 'bg-green-500' },
    { label: 'Good', value: distribution.good, color: 'bg-emerald-400' },
    { label: 'Okay', value: distribution.okay, color: 'bg-yellow-500' },
    { label: 'Not Great', value: distribution.notGreat, color: 'bg-orange-500' },
    { label: 'Poor', value: distribution.poor, color: 'bg-red-500' },
  ];

  const maxValue = Math.max(...bars.map(b => b.value));
  const maxHeight = 32; // pixels for the tallest bar

  return (
    <div className="flex items-end gap-1">
      {bars.map((bar) => {
        const heightPx = maxValue > 0 ? Math.round((bar.value / maxValue) * maxHeight) : 0;
        return (
          <div
            key={bar.label}
            className="flex flex-col items-center gap-0.5"
            title={`${bar.label}: ${bar.value}`}
          >
            <div
              className={`w-4 ${bar.color} rounded-t transition-all`}
              style={{ height: bar.value > 0 ? `${Math.max(heightPx, 4)}px` : '0px' }}
            />
            <span className="text-[9px] text-dark-muted">{bar.value}</span>
          </div>
        );
      })}
    </div>
  );
}

// Small map embed for edit restaurant modal (uses lat/lng or address)
function EditRestaurantMap({
  lat,
  lng,
  address,
}: {
  lat?: number;
  lng?: number;
  address?: string;
}) {
  const mapEmbedUrl = useMemo(() => {
    if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      return `https://www.google.com/maps?q=${lat},${lng}&output=embed`;
    }
    if (address && String(address).trim()) {
      return `https://www.google.com/maps?q=${encodeURIComponent(String(address).trim())}&output=embed`;
    }
    // Default: NYC
    return `https://www.google.com/maps?q=40.7128,-74.006&output=embed`;
  }, [lat, lng, address]);

  return (
    <iframe
      src={mapEmbedUrl}
      className="w-full h-full border-0"
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      title="Restaurant location"
    />
  );
}

// Restaurant Reviews Hover Card Component
function RestaurantReviewsHoverCard({ 
  restaurantName, 
  reviews,
  children 
}: { 
  restaurantName: string;
  reviews: AdminAPI.PostDinnerFeedback[];
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(8),
      flip({
        // Above/below before the opposite side: on a narrow viewport neither
        // side fits the card, and a squeezed side placement lands half off-screen.
        fallbackPlacements: ['bottom-start', 'top-start', 'left-start'],
      }),
      ...viewportClamp(384),
    ],
    whileElementsMounted: autoUpdate,
    placement: 'right-start',
  });

  const hover = useHover(context, {
    delay: { open: 200, close: 100 },
    move: false,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
    role,
  ]);

  if (reviews.length === 0) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="cursor-pointer"
      >
        {children}
      </div>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] w-96 max-w-[calc(100vw-1rem)] bg-dark-surface rounded-lg shadow-2xl border border-dark-border p-4 overflow-y-auto"
          >
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-dark-border">
              <MessageSquare className="w-4 h-4 text-primary-500" />
              <h4 className="font-semibold text-sm">Reviews for {restaurantName}</h4>
              <span className="text-xs text-dark-muted ml-auto">
                {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
              </span>
            </div>

            <div className="space-y-3">
              {reviews.map((review) => (
                <div 
                  key={review.id} 
                  className="p-3 bg-dark-border/30 rounded-lg text-sm"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {review.user?.firstName} {review.user?.lastName}
                      </span>
                      <span className="text-xs text-dark-muted">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {review.overallExperience && (
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs font-medium">{review.overallExperience}/5</span>
                        </div>
                      )}
                      {review.recommendationScore && (
                        <div className="flex items-center gap-1 text-xs">
                          <ThumbsUp className="w-3 h-3 text-accent-500" />
                          <span className="font-medium">{review.recommendationScore}/10</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                    <div>
                      <span className="text-dark-muted">Welcomed: </span>
                      <span className={review.feltWelcomed === 'yes' ? 'text-green-500' : review.feltWelcomed === 'no' ? 'text-red-500' : ''}>
                        {review.feltWelcomed || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-dark-muted">Restaurant: </span>
                      <span>{review.restaurantSelection || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-dark-muted">Location: </span>
                      <span>{review.locationTiming || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-dark-muted">Return: </span>
                      <span className={review.futureAttendance === 'yes' ? 'text-green-500' : review.futureAttendance === 'no' ? 'text-red-500' : ''}>
                        {review.futureAttendance || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {review.notes && (
                    <div className="mt-2 pt-2 border-t border-dark-border/50">
                      <p className="text-xs text-dark-muted italic">"{review.notes}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

export default function AdminNeighborhoods() {
  const [showCreateCity, setShowCreateCity] = useState(false);
  const [showCreateNeighborhood, setShowCreateNeighborhood] = useState(false);
  const [showCreateRestaurant, setShowCreateRestaurant] = useState(false);
  const [editingCity, setEditingCity] = useState<any>(null);
  const [editingNeighborhood, setEditingNeighborhood] = useState<any>(null);
  const [editingRestaurant, setEditingRestaurant] = useState<any>(null);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<
    string | null
  >(null);
  const [selectedType, setSelectedType] = useState<string | null>(null); // Filter by venue type
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<Set<string>>(new Set()); // Multi-select for batch editing
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [batchEditResults, setBatchEditResults] = useState<Record<string, {
    restaurant: any;
    gmapsResult: AdminAPI.GoogleMapsRestaurant | null;
    accepted: boolean;
    loading: boolean;
    error?: string;
  }>>({});
  
  // Google Maps search state (create form)
  const [gmapsQuery, setGmapsQuery] = useState("");
  const [gmapsResults, setGmapsResults] = useState<AdminAPI.GoogleMapsRestaurant[]>([]);
  const [selectedGmapsPlace, setSelectedGmapsPlace] = useState<AdminAPI.GoogleMapsRestaurant | null>(null);
  const [restaurantTags, setRestaurantTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  
  // Google Maps search state (edit form)
  const [editGmapsQuery, setEditGmapsQuery] = useState("");
  const [editGmapsResults, setEditGmapsResults] = useState<AdminAPI.GoogleMapsRestaurant[]>([]);
  const [editRestaurantTags, setEditRestaurantTags] = useState<string[]>([]);
  const [editNewTag, setEditNewTag] = useState("");
  const [mapExpanded, setMapExpanded] = useState(false);
  const mapGetBoundsRef = useRef<(() => MapBounds | null) | null>(null);
  const [viewportSearchMessage, setViewportSearchMessage] = useState<string | null>(null);

  // Server-side restaurant list controls
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [debouncedRestaurantSearch, setDebouncedRestaurantSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedRestaurantSearch(restaurantSearch), 300);
    return () => clearTimeout(timer);
  }, [restaurantSearch]);

  // Any filter change restarts pagination from the first page
  useEffect(() => {
    setPage(1);
  }, [debouncedRestaurantSearch, selectedCityId, selectedNeighborhoodId, selectedType, pageSize]);

  const { data: neighborhoodsData, isLoading } = useAdminNeighborhoods({ includeRestaurants: false });
  const restaurantsQuery = useAdminRestaurants({
    page,
    limit: pageSize,
    search: debouncedRestaurantSearch,
    cityId: selectedCityId,
    neighborhoodId: selectedNeighborhoodId,
    type: selectedType,
  });
  const { data: staticCitiesData } = useStaticCities();
  const { data: restaurantStatsData } = useRestaurantStats();
  const { data: feedbackData } = useAdminFeedback();
  
  // Restaurant stats lookup map
  const restaurantStats = restaurantStatsData?.data || {};
  
  // Build a map of restaurant name to reviews
  const reviewsByRestaurant = useMemo(() => {
    const map: Record<string, AdminAPI.PostDinnerFeedback[]> = {};
    const postDinner = feedbackData?.data?.postDinner || [];
    
    for (const feedback of postDinner) {
      const restaurantName = feedback.booking?.restaurantName;
      if (restaurantName) {
        if (!map[restaurantName]) {
          map[restaurantName] = [];
        }
        map[restaurantName].push(feedback);
      }
    }
    
    // Sort reviews by date (newest first) for each restaurant
    for (const name in map) {
      map[name].sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }
    
    return map;
  }, [feedbackData]);
  const createCity = useCreateCity();
  const createNeighborhood = useCreateNeighborhood();
  const createRestaurant = useCreateRestaurant();
  const updateCity = useUpdateCity();
  const updateNeighborhood = useUpdateNeighborhood();
  const updateRestaurant = useUpdateRestaurant();
  const googleMapsSearch = useGoogleMapsSearch();

  const {
    register: registerCity,
    handleSubmit: handleSubmitCity,
    reset: resetCity,
  } = useForm();
  const {
    register: registerNeighborhood,
    handleSubmit: handleSubmitNeighborhood,
    reset: resetNeighborhood,
  } = useForm();
  const {
    register: registerRestaurant,
    handleSubmit: handleSubmitRestaurant,
    reset: resetRestaurant,
    watch: watchRestaurant,
  } = useForm();
  const {
    register: registerEditCity,
    handleSubmit: handleSubmitEditCity,
    reset: resetEditCity,
    setValue: setEditCityValue,
  } = useForm();
  const {
    register: registerEditNeighborhood,
    handleSubmit: handleSubmitEditNeighborhood,
    reset: resetEditNeighborhood,
    setValue: setEditNeighborhoodValue,
  } = useForm();
  const {
    register: registerEditRestaurant,
    handleSubmit: handleSubmitEditRestaurant,
    reset: resetEditRestaurant,
    setValue: setEditRestaurantValue,
    watch: watchEditRestaurant,
  } = useForm();

  const data = neighborhoodsData?.data || {
    cities: [],
    neighborhoods: [],
    restaurants: [],
  };
  const { cities = [], neighborhoods = [] } = data;

  // Restaurants come pre-filtered & paginated from the server
  const restaurants = restaurantsQuery.data?.data?.restaurants ?? [];
  const restaurantsPagination = restaurantsQuery.data?.data?.pagination;
  const totalRestaurants = restaurantsPagination?.total ?? 0;
  const totalPages = restaurantsPagination?.totalPages ?? 1;


  // Get all available cities from the static data that aren't already in the database
  const allStaticCities = staticCitiesData?.data || [];
  const existingCityIds = new Set(cities.map((c: any) => c.id));
  const availableCitiesToAdd = allStaticCities.filter(c => !existingCityIds.has(c.id));

  // Filter neighborhoods based on selected city, then sort by isActive and then by name
  const filteredNeighborhoods = (
    selectedCityId
      ? neighborhoods.filter((n: any) => n.cityId === selectedCityId)
      : neighborhoods
  ).sort((a, b) => {
    // Sort by isActive (active first), then by name (A-Z)
    if (a.isActive !== b.isActive) {
      return a.isActive ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  // Restaurant filtering (city/neighborhood/type/search) happens server-side;
  // `restaurants` is already the current page of matching venues.
  const filteredRestaurants = restaurants;

  const onCreateCity = (formData: any) => {
    // Look up the city name from the static data if not provided
    const selectedCity = availableCitiesToAdd.find(c => c.id === formData.id);
    const cityData = {
      id: formData.id,
      name: formData.name || selectedCity?.name || formData.id,
    };
    createCity.mutate(cityData, {
      onSuccess: () => {
        setShowCreateCity(false);
        resetCity();
      },
    });
  };

  const onCreateNeighborhood = (formData: any) => {
    createNeighborhood.mutate(formData, {
      onSuccess: () => {
        setShowCreateNeighborhood(false);
        resetNeighborhood();
      },
    });
  };

  const onCreateRestaurant = (formData: any) => {
    // Prioritize Google Maps values if a place was selected
    const finalData = selectedGmapsPlace ? {
      ...formData,
      // Google Maps values take priority
      phone: selectedGmapsPlace.phone !== undefined ? selectedGmapsPlace.phone : formData.phone,
      website: selectedGmapsPlace.website !== undefined ? selectedGmapsPlace.website : formData.website,
      googleMapsLink: selectedGmapsPlace.googleMapsUri !== undefined ? selectedGmapsPlace.googleMapsUri : formData.googleMapsLink,
      googlePlaceId: selectedGmapsPlace.placeId,
      lat: selectedGmapsPlace.lat !== undefined ? selectedGmapsPlace.lat : formData.lat,
      lng: selectedGmapsPlace.lng !== undefined ? selectedGmapsPlace.lng : formData.lng,
      type: formData.type || "restaurant",
      tags: restaurantTags.length > 0 ? restaurantTags : undefined,
    } : {
      ...formData,
      type: formData.type || "restaurant",
      tags: restaurantTags.length > 0 ? restaurantTags : undefined,
    };
    
    createRestaurant.mutate(finalData, {
      onSuccess: () => {
        setShowCreateRestaurant(false);
        resetRestaurant();
        setSelectedGmapsPlace(null);
        setGmapsResults([]);
        setGmapsQuery("");
        setRestaurantTags([]);
      },
    });
  };
  
  const handleGmapsSearch = async () => {
    if (!gmapsQuery.trim()) return;
    setViewportSearchMessage(null);
    const result = await googleMapsSearch.mutateAsync({ query: gmapsQuery });
    if (result.success && result.data) {
      setGmapsResults(result.data.restaurants);
    }
  };

  const handleGmapsSearchInViewport = async () => {
    if (!gmapsQuery.trim()) return;
    setViewportSearchMessage(null);
    const getBounds = mapGetBoundsRef.current;
    const bounds = getBounds?.() ?? null;
    if (!bounds) {
      setViewportSearchMessage("Expand the map above and position it, then try again.");
      return;
    }
    const result = await googleMapsSearch.mutateAsync({ query: gmapsQuery, bounds });
    if (result.success && result.data) {
      setGmapsResults(result.data.restaurants);
    }
  };
  
  const selectGmapsPlace = (place: AdminAPI.GoogleMapsRestaurant) => {
    setSelectedGmapsPlace(place);
    setRestaurantTags(place.tags);
    
    // Classify from Google's primaryType (single canonical type) rather than
    // the full `types` array — a restaurant that has a bar lists both, so
    // scanning `types` mis-classifies it as a bar.
    const primary = place.primaryType;
    let venueType = "restaurant";
    if (primary === "bar" || primary === "cocktail_bar" || primary === "wine_bar" || primary === "pub" || primary === "night_club") {
      venueType = "bar";
    } else if (primary === "cafe" || primary === "coffee_shop") {
      venueType = "cafe";
    }
    
    // Auto-fill form fields
    resetRestaurant({
      name: place.name,
      address: place.address,
      type: venueType,
      lat: place.lat,
      lng: place.lng,
      phone: place.phone || "",
      website: place.website || "",
      googleMapsLink: place.googleMapsUri || "",
    });
    setGmapsResults([]);
  };
  
  const addTag = () => {
    if (newTag.trim() && !restaurantTags.includes(newTag.trim())) {
      setRestaurantTags([...restaurantTags, newTag.trim()]);
      setNewTag("");
    }
  };
  
  const removeTag = (tag: string) => {
    setRestaurantTags(restaurantTags.filter(t => t !== tag));
  };
  
  const addEditTag = () => {
    if (editNewTag.trim() && !editRestaurantTags.includes(editNewTag.trim())) {
      setEditRestaurantTags([...editRestaurantTags, editNewTag.trim()]);
      setEditNewTag("");
    }
  };
  
  const removeEditTag = (tag: string) => {
    setEditRestaurantTags(editRestaurantTags.filter(t => t !== tag));
  };
  
  const handleEditGmapsSearch = async () => {
    if (!editGmapsQuery.trim()) return;
    
    const result = await googleMapsSearch.mutateAsync({ query: editGmapsQuery });
    if (result.success && result.data) {
      setEditGmapsResults(result.data.restaurants);
    }
  };
  
  const selectEditGmapsPlace = (place: AdminAPI.GoogleMapsRestaurant) => {
    const primary = place.primaryType;
    let venueType = "restaurant";
    if (primary === "bar" || primary === "cocktail_bar" || primary === "wine_bar" || primary === "pub" || primary === "night_club") {
      venueType = "bar";
    } else if (primary === "cafe" || primary === "coffee_shop") {
      venueType = "cafe";
    }
    
    // Always prioritize Google Maps values when explicitly selected
    if (place.address) {
      setEditRestaurantValue("address", place.address);
    }
    if (place.phone !== undefined) {
      setEditRestaurantValue("phone", place.phone || "");
    }
    if (place.website !== undefined) {
      setEditRestaurantValue("website", place.website || "");
    }
    if (place.googleMapsUri !== undefined) {
      setEditRestaurantValue("googleMapsLink", place.googleMapsUri || "");
    }
    // Always update lat/lng from Google Maps if available
    if (place.lat !== undefined) {
      setEditRestaurantValue("lat", place.lat);
    }
    if (place.lng !== undefined) {
      setEditRestaurantValue("lng", place.lng);
    }
    // Always use Google Maps determined type
    setEditRestaurantValue("type", venueType);
    // Replace tags with Google Maps tags (they take priority)
    if (place.tags && place.tags.length > 0) {
      setEditRestaurantTags(place.tags);
    }
  };

  const onEditCity = (formData: any) => {
    if (!editingCity) return;
    updateCity.mutate(
      { id: editingCity.id, data: formData },
      {
        onSuccess: () => {
          setEditingCity(null);
          resetEditCity();
        },
      }
    );
  };

  const onEditNeighborhood = (formData: any) => {
    if (!editingNeighborhood) return;
    updateNeighborhood.mutate(
      { id: editingNeighborhood.id, data: formData },
      {
        onSuccess: () => {
          setEditingNeighborhood(null);
          resetEditNeighborhood();
        },
      }
    );
  };

  const onEditRestaurant = (formData: any) => {
    if (!editingRestaurant) return;
    updateRestaurant.mutate(
      { 
        id: editingRestaurant.id, 
        data: {
          ...formData,
          type: formData.type || "restaurant",
          lat: formData.lat !== undefined && formData.lat !== "" ? formData.lat : null,
          lng: formData.lng !== undefined && formData.lng !== "" ? formData.lng : null,
          tags: editRestaurantTags,
        }
      },
      {
        onSuccess: () => {
          setEditingRestaurant(null);
          resetEditRestaurant();
          setEditRestaurantTags([]);
        },
      }
    );
  };

  const handleEditCity = (city: any) => {
    setEditingCity(city);
    setEditCityValue("name", city.name);
    setEditCityValue("isActive", city.isActive);
  };

  const handleEditNeighborhood = (neighborhood: any) => {
    setEditingNeighborhood(neighborhood);
    setEditNeighborhoodValue("name", neighborhood.name);
    setEditNeighborhoodValue("cityId", neighborhood.cityId);
    setEditNeighborhoodValue("isActive", neighborhood.isActive);
    setEditNeighborhoodValue("launchedAfter", neighborhood.launchedAfter || "");
    setEditNeighborhoodValue("freeUntil", neighborhood.freeUntil || "");
  };

  const handleEditRestaurant = (restaurant: any) => {
    setEditingRestaurant(restaurant);
    setEditRestaurantValue("name", restaurant.name);
    setEditRestaurantValue("address", restaurant.address);
    setEditRestaurantValue("neighborhoodId", restaurant.neighborhoodId);
    setEditRestaurantValue("type", restaurant.type || "restaurant");
    setEditRestaurantValue("lat", restaurant.lat || "");
    setEditRestaurantValue("lng", restaurant.lng || "");
    setEditRestaurantValue("phone", restaurant.phone || "");
    setEditRestaurantValue("website", restaurant.website || "");
    setEditRestaurantValue("googleMapsLink", restaurant.googleMapsLink || "");
    setEditRestaurantValue("bookingLink", restaurant.bookingLink || "");
    setEditRestaurantValue("isActive", restaurant.isActive);
    setEditRestaurantTags(restaurant.tags || []);
    // Pre-fill Google Maps search query with restaurant name and address
    setEditGmapsQuery(`${restaurant.name} ${restaurant.address}`);
    setEditGmapsResults([]);
  };

  // Normalize string for title/address comparison (trim, lowercase, collapse spaces)
  const normalizeForCompare = (s: string) =>
    (s || "").trim().toLowerCase().replace(/\s+/g, " ");

  const handleBatchUpdate = async () => {
    if (selectedRestaurantIds.size === 0) return;
    
    setShowBatchEdit(true);
    const results: Record<string, {
      restaurant: any;
      gmapsResult: AdminAPI.GoogleMapsRestaurant | null;
      accepted: boolean;
      loading: boolean;
      error?: string;
    }> = {};

    // Initialize results with selected restaurants
    for (const id of selectedRestaurantIds) {
      const restaurant = restaurants.find((r: any) => r.id === id);
      if (restaurant) {
        results[id] = {
          restaurant,
          gmapsResult: null,
          accepted: false,
          loading: true,
        };
      }
    }
    setBatchEditResults(results);

    // Search Google Maps for each restaurant
    for (const id of selectedRestaurantIds) {
      const restaurant = restaurants.find((r: any) => r.id === id);
      if (!restaurant) continue;

      try {
        const query = `${restaurant.name} ${restaurant.address}`;
        const result = await googleMapsSearch.mutateAsync({ query });
        
        if (result.success && result.data?.restaurants && result.data.restaurants.length > 0) {
          // Use the first result (most relevant)
          const gmapsResult = result.data.restaurants[0];
          // Auto-select if title and address match (normalized)
          const nameMatch = normalizeForCompare(restaurant.name) === normalizeForCompare(gmapsResult.name);
          const addressMatch = normalizeForCompare(restaurant.address) === normalizeForCompare(gmapsResult.address);
          const autoAccept = nameMatch && addressMatch;

          setBatchEditResults(prev => ({
            ...prev,
            [id]: {
              ...prev[id],
              gmapsResult,
              loading: false,
              accepted: autoAccept,
            },
          }));
        } else {
          setBatchEditResults(prev => ({
            ...prev,
            [id]: {
              ...prev[id],
              loading: false,
              error: "No results found",
            },
          }));
        }
      } catch (error) {
        setBatchEditResults(prev => ({
          ...prev,
          [id]: {
            ...prev[id],
            loading: false,
            error: "Search failed",
          },
        }));
      }
    }
  };

  const toggleBatchAccept = (restaurantId: string) => {
    setBatchEditResults(prev => ({
      ...prev,
      [restaurantId]: {
        ...prev[restaurantId],
        accepted: !prev[restaurantId].accepted,
      },
    }));
  };

  const applyBatchUpdates = async () => {
    const updates = Object.entries(batchEditResults)
      .filter(([_, result]) => result.accepted && result.gmapsResult)
      .map(([id, result]) => {
        const gmaps = result.gmapsResult!;
        
        const primary = gmaps.primaryType;
        let venueType = "restaurant";
        if (primary === "bar" || primary === "cocktail_bar" || primary === "wine_bar" || primary === "pub" || primary === "night_club") {
          venueType = "bar";
        } else if (primary === "cafe" || primary === "coffee_shop") {
          venueType = "cafe";
        }

        return {
          id,
          data: {
            // Always prioritize Google Maps values - use them even if empty/null
            phone: gmaps.phone !== undefined ? gmaps.phone : result.restaurant.phone || null,
            website: gmaps.website !== undefined ? gmaps.website : result.restaurant.website || null,
            googleMapsLink: gmaps.googleMapsUri !== undefined ? gmaps.googleMapsUri : result.restaurant.googleMapsLink || null,
            googlePlaceId: gmaps.placeId !== undefined ? gmaps.placeId : result.restaurant.googlePlaceId || null,
            type: venueType, // Always use Google Maps determined type
            lat: gmaps.lat !== undefined ? gmaps.lat : result.restaurant.lat,
            lng: gmaps.lng !== undefined ? gmaps.lng : result.restaurant.lng,
            tags: gmaps.tags && gmaps.tags.length > 0 ? gmaps.tags : result.restaurant.tags || [],
          },
        };
      });

    // Apply updates sequentially
    let successCount = 0;
    let errorCount = 0;
    
    for (const update of updates) {
      try {
        await updateRestaurant.mutateAsync(update);
        successCount++;
      } catch (error) {
        console.error(`Failed to update restaurant ${update.id}:`, error);
        errorCount++;
      }
    }

    // Show feedback
    if (successCount > 0) {
      alert(`Successfully updated ${successCount} restaurant${successCount > 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
    }

    // Close modal and clear selection
    setShowBatchEdit(false);
    setSelectedRestaurantIds(new Set());
    setBatchEditResults({});
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Neighborhoods</h1>
          <p className="text-dark-muted text-lg">
            Manage cities, neighborhoods, and restaurant venues
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateCity(!showCreateCity)}
            className="btn btn-sm btn-ghost border border-dark-border"
          >
            <Plus className="w-3 h-3 mr-1" />
            City
          </button>
          <button
            onClick={() => setShowCreateNeighborhood(!showCreateNeighborhood)}
            className="btn btn-sm btn-ghost border border-dark-border"
          >
            <Plus className="w-3 h-3 mr-1" />
            Neighborhood
          </button>
        </div>
      </motion.div>

      {/* Create City Form */}
      {showCreateCity && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="card mb-6"
          >
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-primary-500" />
              Add City
            </h3>
            <form
              onSubmit={handleSubmitCity(onCreateCity)}
              className="space-y-3"
            >
              <div>
                <label className="label text-xs">Select City to Add</label>
                {availableCitiesToAdd.length > 0 ? (
                  <select
                    {...registerCity("id", { required: true })}
                    className="select select-sm select-bordered w-full"
                  >
                    <option value="">Select a city...</option>
                    {availableCitiesToAdd.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name} {city.emoji} 
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-dark-muted">All available cities have been added.</p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={createCity.isPending || availableCitiesToAdd.length === 0}
                >
                  {createCity.isPending ? "Creating..." : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateCity(false)}
                  className="btn btn-sm btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

      {/* Create Neighborhood Form */}
      {showCreateNeighborhood && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="card mb-6"
          >
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
              <Building2 className="w-5 h-5 text-accent-500" />
              Add Neighborhood
            </h3>
            <form
              onSubmit={handleSubmitNeighborhood(onCreateNeighborhood)}
              className="space-y-3"
            >
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label text-xs">Neighborhood ID</label>
                  <input
                    {...registerNeighborhood("id", { required: true })}
                    type="text"
                    className="input input-sm w-full"
                    placeholder="e.g., chelsea"
                  />
                </div>
                <div>
                  <label className="label text-xs">Name</label>
                  <input
                    {...registerNeighborhood("name", { required: true })}
                    type="text"
                    className="input input-sm w-full"
                    placeholder="e.g., Chelsea"
                  />
                </div>
                <div>
                  <label className="label text-xs">City</label>
                  <select
                    {...registerNeighborhood("cityId", { required: true })}
                    className="select select-sm select-bordered w-full"
                  >
                    <option value="">Select city</option>
                    {cities.map((city: any) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="label text-xs">Launched After Date (optional)</label>
                <input
                  {...registerNeighborhood("launchedAfter")}
                  type="date"
                  className="input input-sm w-full"
                  placeholder="YYYY-MM-DD"
                />
                <p className="text-xs text-dark-muted mt-1">
                  Neighborhood will be enabled after this event date
                </p>
              </div>
              <div>
                <label className="label text-xs">Free Until Date (optional)</label>
                <input
                  {...registerNeighborhood("freeUntil")}
                  type="date"
                  className="input input-sm w-full"
                  placeholder="YYYY-MM-DD"
                />
                <p className="text-xs text-dark-muted mt-1">
                  Events on or before this date will not require payment
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm btn-primary"
                  disabled={createNeighborhood.isPending}
                >
                  {createNeighborhood.isPending ? "Creating..." : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateNeighborhood(false)}
                  className="btn btn-sm btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

      {/* Restaurants Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold">Restaurants</h2>
            {selectedRestaurantIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-dark-muted">
                  {selectedRestaurantIds.size} selected
                </span>
                <button
                  onClick={() => {
                    setSelectedRestaurantIds(new Set());
                  }}
                  className="btn btn-sm btn-ghost"
                >
                  Clear
                </button>
                <button
                  onClick={() => handleBatchUpdate()}
                  className="btn btn-sm btn-primary"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Batch Update from Google Maps
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {filteredRestaurants.length > 0 && (
              <button
                onClick={() => {
                  const allSelected = filteredRestaurants.every((r: any) =>
                    selectedRestaurantIds.has(r.id)
                  );
                  if (allSelected) {
                    // Deselect all filtered restaurants
                    const newSet = new Set(selectedRestaurantIds);
                    filteredRestaurants.forEach((r: any) => {
                      newSet.delete(r.id);
                    });
                    setSelectedRestaurantIds(newSet);
                  } else {
                    // Select all filtered restaurants
                    const newSet = new Set(selectedRestaurantIds);
                    filteredRestaurants.forEach((r: any) => {
                      newSet.add(r.id);
                    });
                    setSelectedRestaurantIds(newSet);
                  }
                }}
                className="btn btn-ghost"
              >
                {filteredRestaurants.every((r: any) =>
                  selectedRestaurantIds.has(r.id)
                )
                  ? "Deselect All"
                  : "Select All"}
              </button>
            )}
            <button
              onClick={() => setShowCreateRestaurant(!showCreateRestaurant)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2 inline-block" />
              Add
            </button>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="card mb-4">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 text-dark-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={restaurantSearch}
                onChange={(e) => setRestaurantSearch(e.target.value)}
                className="input w-full pl-9 pr-9"
                placeholder="Search restaurants by name or address..."
              />
              {restaurantSearch && (
                <button
                  type="button"
                  onClick={() => setRestaurantSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-dark-border rounded"
                  title="Clear search"
                >
                  <X className="w-4 h-4 text-dark-muted" />
                </button>
              )}
            </div>
            <FilterDropdown
              label="All Cities"
              icon={<MapPin className="w-3.5 h-3.5" />}
              value={selectedCityId ?? ""}
              options={[
                { value: "", label: "All Cities" },
                ...cities.map((city: any) => ({
                  value: city.id,
                  label: city.name,
                })),
              ]}
              onChange={(value) => {
                setSelectedCityId(value || null);
                setSelectedNeighborhoodId(null);
              }}
            />
            {selectedCityId && (
              <button
                onClick={() => {
                  const city = cities.find((c: any) => c.id === selectedCityId);
                  if (city) handleEditCity(city);
                }}
                className="btn btn-sm btn-ghost btn-square"
                title="Edit selected city"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
            <FilterDropdown
              label="All Neighborhoods"
              icon={<Building2 className="w-3.5 h-3.5" />}
              value={selectedNeighborhoodId ?? ""}
              options={[
                { value: "", label: "All Neighborhoods" },
                ...filteredNeighborhoods.map((neighborhood: any) => ({
                  value: neighborhood.id,
                  label:
                    !selectedCityId && neighborhood.cityName
                      ? `${neighborhood.cityName} - ${neighborhood.name}`
                      : neighborhood.name,
                })),
              ]}
              onChange={(value) => setSelectedNeighborhoodId(value || null)}
            />
            {selectedNeighborhoodId && (
              <button
                onClick={() => {
                  const neighborhood = neighborhoods.find(
                    (n: any) => n.id === selectedNeighborhoodId
                  );
                  if (neighborhood) handleEditNeighborhood(neighborhood);
                }}
                className="btn btn-sm btn-ghost btn-square"
                title="Edit selected neighborhood"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <UtensilsCrossed className="w-4 h-4 text-green-500" />
            <h3 className="text-sm font-semibold">Filter by Type</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedType(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                !selectedType
                  ? "bg-green-500 text-white shadow-lg"
                  : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              All Types
            </button>
            {["restaurant", "bar", "cafe"].map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(selectedType === type ? null : type)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all capitalize ${
                  selectedType === type
                    ? "bg-green-500 text-white shadow-lg"
                    : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Overall map: visible venues + Google Maps search results when creating a restaurant */}
        {(() => {
          const hasValidCoords = (r: { lat?: number | null; lng?: number | null }) =>
            r.lat != null && r.lng != null && (Math.abs(Number(r.lat)) > 1e-5 || Math.abs(Number(r.lng)) > 1e-5);
          const venuesWithCoords = filteredRestaurants.filter((r: any) => hasValidCoords(r));
          const gmapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
          const photoMediaUrl = (photoName?: string) =>
            photoName && gmapsApiKey
              ? `https://places.googleapis.com/v1/${photoName}/media?maxHeightPx=240&key=${gmapsApiKey}`
              : null;
          const searchResultsAsVenues =
            showCreateRestaurant && gmapsResults.length > 0
              ? gmapsResults
                  .filter((p) => hasValidCoords(p))
                  .map((place) => ({
                    id: `gmaps-${place.placeId}`,
                    name: place.name,
                    address: place.address,
                    lat: place.lat,
                    lng: place.lng,
                    phone: place.phone,
                    website: place.website,
                    googleMapsLink: place.googleMapsUri,
                    description: place.description,
                    photoUrl: photoMediaUrl(place.photoName),
                    rating: place.rating,
                    userRatingCount: place.userRatingCount,
                    priceLevel: place.priceLevel,
                    tags: place.tags,
                    type:
                      place.types?.includes("bar") ||
                      place.types?.includes("cocktail_bar") ||
                      place.types?.includes("wine_bar") ||
                      place.types?.includes("pub")
                        ? "bar"
                        : place.types?.includes("cafe") || place.types?.includes("coffee_shop")
                          ? "cafe"
                          : "restaurant",
                  }))
              : [];
          const allVenues = [...filteredRestaurants, ...searchResultsAsVenues];
          if (filteredRestaurants.length === 0 && searchResultsAsVenues.length === 0) return null;
          return (
            <div className="card mb-4">
              <button
                type="button"
                onClick={() => setMapExpanded((v) => !v)}
                className="flex w-full items-center justify-between gap-2 rounded-lg py-1 -my-1 hover:bg-dark-border/30 transition-colors text-left"
              >
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  {mapExpanded ? (
                    <ChevronDown className="w-4 h-4 text-primary-500 shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-primary-500 shrink-0" />
                  )}
                  <MapPin className="w-4 h-4 text-primary-500" />
                  Map – {venuesWithCoords.length} of {filteredRestaurants.length} venues
                  {searchResultsAsVenues.length > 0 && (
                    <span className="text-dark-muted font-normal">
                      {" "}
                      + {searchResultsAsVenues.length} search result{searchResultsAsVenues.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </h3>
              </button>
              {mapExpanded && (
                <div className="mt-2">
                  <OverallVenuesMap
                    venues={allVenues}
                    height="32rem"
                    className="rounded-lg overflow-hidden border border-dark-border"
                    onMapReady={(getBounds) => {
                      mapGetBoundsRef.current = getBounds;
                    }}
                  />
                </div>
              )}
            </div>
          );
        })()}

        {/* Create Restaurant Form */}
        {showCreateRestaurant && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="card mb-4"
          >
            <h3 className="text-lg font-bold mb-4">Create New Restaurant</h3>
            
            {/* Google Maps Search */}
            <div className="bg-dark-border/30 rounded-lg p-4 mb-4">
              <label className="label flex items-center gap-2">
                <Search className="w-4 h-4" />
                Search Google Maps
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={gmapsQuery}
                  onChange={(e) => setGmapsQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleGmapsSearch())}
                  className="input flex-1"
                  placeholder="Search for a restaurant (e.g., 'Blue Hill NYC')"
                />
                <button
                  type="button"
                  onClick={handleGmapsSearch}
                  className="btn btn-primary"
                  disabled={googleMapsSearch.isPending}
                >
                  {googleMapsSearch.isPending ? "Searching..." : "Search"}
                </button>
                <button
                  type="button"
                  onClick={handleGmapsSearchInViewport}
                  className="btn btn-secondary"
                  disabled={googleMapsSearch.isPending}
                  title="Restrict results to the visible map area"
                >
                  {googleMapsSearch.isPending ? "Searching..." : "Search in viewport"}
                </button>
              </div>
              {viewportSearchMessage && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">{viewportSearchMessage}</p>
              )}
              <p className="text-xs text-dark-muted mt-2">
                {gmapsResults.some((p) => p.lat != null && p.lng != null)
                  ? "Search results appear on the map above (hover for details)."
                  : "Search for a place to see results below and on the map above."}
              </p>
              
              {/* Google Maps Search Results */}
              {gmapsResults.length > 0 && (
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                  {gmapsResults.map((place) => (
                    <div
                      key={place.placeId}
                      onClick={() => selectGmapsPlace(place)}
                      className="p-3 bg-dark-surface rounded-lg cursor-pointer hover:bg-dark-border/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{place.name}</p>
                          <p className="text-sm text-dark-muted">{place.address}</p>
                          {place.rating && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span className="text-sm">{place.rating}</span>
                              {place.userRatingCount && (
                                <span className="text-xs text-dark-muted">
                                  ({place.userRatingCount} reviews)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                        >
                          Select
                        </button>
                      </div>
                      {place.tags.length > 0 && (
                        <div className="relative group/tags">
                          <div className="flex flex-wrap gap-1 mt-2">
                            {place.tags.slice(0, 6).map((tag) => (
                              <span
                                key={tag}
                                className="px-1.5 py-0.5 bg-primary-500/10 text-primary-500 text-xs rounded"
                              >
                                {tag.replace(/_/g, " ")}
                              </span>
                            ))}
                            {place.tags.length > 6 && (
                              <span className="text-xs text-dark-muted cursor-help">+{place.tags.length - 6} more</span>
                            )}
                          </div>
                          {place.tags.length > 6 && (
                            <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/tags:block">
                              <div className="bg-dark-surface border border-dark-border rounded-lg p-2 shadow-xl max-w-xs">
                                <div className="flex flex-wrap gap-1">
                                  {place.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-1.5 py-0.5 bg-primary-500/10 text-primary-500 text-xs rounded"
                                    >
                                      {tag.replace(/_/g, " ")}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {selectedGmapsPlace && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-500">Selected from Google Maps:</p>
                      <p className="text-sm">{selectedGmapsPlace.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedGmapsPlace(null);
                        setRestaurantTags([]);
                        resetRestaurant();
                      }}
                      className="p-1 hover:bg-dark-border rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <form
              onSubmit={handleSubmitRestaurant(onCreateRestaurant)}
              className="space-y-4"
            >
              <div>
                <label className="label">Restaurant Name</label>
                <input
                  {...registerRestaurant("name", { required: true })}
                  type="text"
                  className="input"
                  placeholder="e.g., Blue Hill"
                />
              </div>
              <div>
                <label className="label">Address</label>
                <input
                  {...registerRestaurant("address", { required: true })}
                  type="text"
                  className="input"
                  placeholder="e.g., 75 Washington Place"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Neighborhood</label>
                  <select
                    {...registerRestaurant("neighborhoodId", {
                      required: true,
                    })}
                    className="input"
                  >
                    <option value="">Select a neighborhood</option>
                    {neighborhoods.map((neighborhood: any) => (
                      <option key={neighborhood.id} value={neighborhood.id}>
                        {neighborhood.name} ({neighborhood.cityName})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Type</label>
                  <select
                    {...registerRestaurant("type")}
                    className="input"
                    defaultValue="restaurant"
                  >
                    <option value="restaurant">Restaurant</option>
                    <option value="bar">Bar</option>
                    <option value="cafe">Cafe</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Location Coordinates</label>
                  {watchRestaurant("address") && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(watchRestaurant("address"))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-500 hover:underline"
                    >
                      Pick on Map →
                    </a>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="label text-xs">Latitude (optional)</label>
                    <input
                      {...registerRestaurant("lat", { valueAsNumber: true })}
                      type="number"
                      step="any"
                      className="input"
                      placeholder="e.g., 40.7282"
                    />
                  </div>
                  <div>
                    <label className="label text-xs">Longitude (optional)</label>
                    <input
                      {...registerRestaurant("lng", { valueAsNumber: true })}
                      type="number"
                      step="any"
                      className="input"
                      placeholder="e.g., -73.9942"
                    />
                  </div>
                </div>
                {watchRestaurant("lat") && watchRestaurant("lng") && (
                  <div className="mt-2">
                    <a
                      href={`https://www.google.com/maps?q=${watchRestaurant("lat")},${watchRestaurant("lng")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-500 hover:underline"
                    >
                      View on Google Maps →
                    </a>
                  </div>
                )}
              </div>
               <div className="grid md:grid-cols-2 gap-4">
                 <div>
                   <label className="label">Phone (optional)</label>
                   <input
                     {...registerRestaurant("phone")}
                     type="text"
                     className="input"
                     placeholder="e.g., (212) 555-1234"
                   />
                 </div>
                 <div>
                   <label className="label">Website (optional)</label>
                   <input
                     {...registerRestaurant("website")}
                     type="url"
                     className="input"
                     placeholder="e.g., https://example.com"
                   />
                 </div>
               </div>
               <div className="grid md:grid-cols-2 gap-4">
                 <div>
                   <label className="label">Google Maps Link (optional)</label>
                   <input
                     {...registerRestaurant("googleMapsLink")}
                     type="url"
                     className="input"
                     placeholder="e.g., https://goo.gl/maps/..."
                   />
                 </div>
                 <div>
                   <label className="label">Booking Link (optional)</label>
                   <input
                     {...registerRestaurant("bookingLink")}
                     type="url"
                     className="input"
                     placeholder="e.g., OpenTable, Resy link"
                   />
                 </div>
               </div>
               
               {/* Tags Section */}
               <div>
                 <label className="label flex items-center gap-2">
                   <Tag className="w-4 h-4" />
                   Tags (for matching preferences)
                 </label>
                 <div className="flex flex-wrap gap-2 mb-2">
                   {restaurantTags.map((tag) => (
                     <span
                       key={tag}
                       className="px-2 py-1 bg-primary-500/20 text-primary-500 text-sm rounded-full flex items-center gap-1"
                     >
                       {tag.replace(/_/g, " ")}
                       <button
                         type="button"
                         onClick={() => removeTag(tag)}
                         className="hover:bg-primary-500/30 rounded-full p-0.5"
                       >
                         <X className="w-3 h-3" />
                       </button>
                     </span>
                   ))}
                 </div>
                 <div className="flex gap-2">
                   <input
                     type="text"
                     value={newTag}
                     onChange={(e) => setNewTag(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                     className="input flex-1"
                     placeholder="Add a tag (e.g., italian, has_vegetarian_options)"
                   />
                   <button
                     type="button"
                     onClick={addTag}
                     className="btn btn-ghost"
                   >
                     Add
                   </button>
                 </div>
                 <p className="text-xs text-dark-muted mt-1">
                   Common tags: italian, mexican, upscale, budget_friendly, has_vegetarian_options, has_vegan_options, outdoor_seating
                 </p>
               </div>
               
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createRestaurant.isPending}
                >
                  {createRestaurant.isPending
                    ? "Creating..."
                    : "Create Restaurant"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateRestaurant(false);
                    setSelectedGmapsPlace(null);
                    setGmapsResults([]);
                    setGmapsQuery("");
                    setRestaurantTags([]);
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        <div className={`space-y-4 transition-opacity ${restaurantsQuery.isFetching ? "opacity-60" : ""}`}>
          {restaurantsQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader />
            </div>
          ) : filteredRestaurants.length === 0 ? (
            <div className="card text-center py-12 text-dark-muted">
              No restaurants match the current filters.
            </div>
          ) : null}
          {filteredRestaurants.map((restaurant: any, index: number) => {
            const stats = restaurantStats[restaurant.id];
            return (
              <motion.div
                key={restaurant.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <button
                      onClick={() => {
                        const newSet = new Set(selectedRestaurantIds);
                        if (newSet.has(restaurant.id)) {
                          newSet.delete(restaurant.id);
                        } else {
                          newSet.add(restaurant.id);
                        }
                        setSelectedRestaurantIds(newSet);
                      }}
                      className="mt-1 flex-shrink-0 p-1 hover:bg-dark-border rounded transition-colors"
                      title="Select for batch edit"
                    >
                      {selectedRestaurantIds.has(restaurant.id) ? (
                        <CheckSquare className="w-5 h-5 text-primary-500" />
                      ) : (
                        <Square className="w-5 h-5 text-dark-muted" />
                      )}
                    </button>
                    <UtensilsCrossed className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{restaurant.name}</h3>
                        {restaurant.type && restaurant.type !== "restaurant" && (
                          <span className="px-2 py-0.5 bg-primary-500/20 text-primary-500 text-xs rounded-full capitalize">
                            {restaurant.type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-dark-muted mb-2">
                        {restaurant.address}
                      </p>
                      <div className="flex flex-wrap gap-2 text-sm">
                        <span className="text-dark-muted">
                          📍 {restaurant.neighborhoodName}
                        </span>
                        {restaurant.phone && (
                          <span className="text-dark-muted">
                            📞 {restaurant.phone}
                          </span>
                        )}
                      </div>
                      {/* Display restaurant tags */}
                      {restaurant.tags && restaurant.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {restaurant.tags.map((tag: string) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-primary-500/10 text-primary-500 text-xs rounded-full"
                            >
                              {tag.replace(/_/g, " ")}
                            </span>
                          ))}
                        </div>
                      )}
                       <div className="flex flex-wrap gap-3 mt-2">
                         {restaurant.website && (
                           <a
                             href={restaurant.website}
                             target="_blank"
                             rel="noopener noreferrer"
                             className="text-primary-500 text-sm hover:underline"
                           >
                             Website →
                           </a>
                         )}
                        {restaurant.bookingLink && (
                          <a
                            href={restaurant.bookingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-500 text-sm hover:underline font-medium"
                          >
                            Book Table →
                          </a>
                        )}
                       </div>
                    </div>
                  </div>
                  
                  {/* Restaurant Analytics */}
                  <div className="flex items-start gap-4 flex-shrink-0">
                    {stats && stats.responseCount > 0 && (
                      <RestaurantReviewsHoverCard 
                        restaurantName={restaurant.name} 
                        reviews={reviewsByRestaurant[restaurant.name] || []}
                      >
                        <div className="flex items-center gap-4 bg-dark-border/30 rounded-lg px-3 py-2 hover:bg-dark-border/50 transition-colors">
                          {/* Rating Histogram */}
                          <div className="flex flex-col items-center">
                            <div className="flex items-center gap-1 mb-1">
                              <BarChart3 className="w-3 h-3 text-dark-muted" />
                              <span className="text-xs text-dark-muted">Ratings</span>
                            </div>
                            <RatingHistogram distribution={stats.ratingDistribution} />
                          </div>
                          
                          {/* NPS Score */}
                          {stats.avgNps !== null && (
                            <div className="text-center border-l border-dark-border pl-4">
                              <div className="flex items-center gap-1 mb-1">
                                <ThumbsUp className="w-3 h-3 text-dark-muted" />
                                <span className="text-xs text-dark-muted">NPS</span>
                              </div>
                              <div className="text-xl font-bold text-accent-500">
                                {stats.avgNps.toFixed(1)}
                              </div>
                              <div className="text-[10px] text-dark-muted">
                                {stats.responseCount} {stats.responseCount === 1 ? 'response' : 'responses'}
                              </div>
                            </div>
                          )}
                          
                          {/* Avg Experience */}
                          {stats.avgExperience !== null && (
                            <div className="text-center border-l border-dark-border pl-4">
                              <div className="flex items-center gap-1 mb-1">
                                <Star className="w-3 h-3 text-dark-muted" />
                                <span className="text-xs text-dark-muted">Exp</span>
                              </div>
                              <div className="text-xl font-bold text-primary-500">
                                {stats.avgExperience.toFixed(1)}
                              </div>
                              <div className="text-[10px] text-dark-muted">/5</div>
                            </div>
                          )}
                        </div>
                      </RestaurantReviewsHoverCard>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditRestaurant(restaurant)}
                        className="p-2 hover:bg-dark-border rounded-lg transition-colors"
                        title="Edit restaurant"
                      >
                        <Edit2 className="w-4 h-4 text-dark-muted" />
                      </button>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                          restaurant.isActive
                            ? "bg-green-500/20 text-green-500"
                            : "bg-red-500/20 text-red-500"
                        }`}
                      >
                        {restaurant.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalRestaurants > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <span className="text-sm text-dark-muted">
              Showing {(page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, totalRestaurants)} of {totalRestaurants}
            </span>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="select select-sm select-bordered"
                title="Restaurants per page"
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || restaurantsQuery.isFetching}
                className="btn btn-sm btn-ghost"
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </button>
              <span className="text-sm text-dark-muted whitespace-nowrap">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || restaurantsQuery.isFetching}
                className="btn btn-sm btn-ghost"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Edit City Modal */}
      {editingCity && (
        <div
          className="modal modal-open"
          onClick={() => {
            setEditingCity(null);
            resetEditCity();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="modal-box max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-xl mb-4">
              Edit City: {editingCity.name}
            </h3>
            <form
              onSubmit={handleSubmitEditCity(onEditCity)}
              className="space-y-4"
            >
              <div>
                <label className="label">City Name</label>
                <input
                  {...registerEditCity("name")}
                  type="text"
                  className="input w-full"
                  placeholder="e.g., New York City"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  {...registerEditCity("isActive")}
                  type="checkbox"
                  id="editCityActive"
                  className="checkbox"
                />
                <label htmlFor="editCityActive" className="label mb-0">
                  Active
                </label>
              </div>
              <div className="modal-action">
                <button
                  type="button"
                  onClick={() => {
                    setEditingCity(null);
                    resetEditCity();
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateCity.isPending}
                >
                  {updateCity.isPending ? "Updating..." : "Update City"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit Neighborhood Modal */}
      {editingNeighborhood && (
        <div
          className="modal modal-open"
          onClick={() => {
            setEditingNeighborhood(null);
            resetEditNeighborhood();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="modal-box max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-xl mb-4">
              Edit Neighborhood: {editingNeighborhood.name}
            </h3>
            <form
              onSubmit={handleSubmitEditNeighborhood(onEditNeighborhood)}
              className="space-y-4"
            >
              <div>
                <label className="label">Neighborhood ID</label>
                <input
                  type="text"
                  value={editingNeighborhood.id}
                  disabled
                  readOnly
                  className="input w-full opacity-60 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="label">Neighborhood Name</label>
                <input
                  {...registerEditNeighborhood("name")}
                  type="text"
                  className="input w-full"
                  placeholder="e.g., Chelsea"
                />
              </div>
              <div>
                <label className="label">City</label>
                <select
                  {...registerEditNeighborhood("cityId")}
                  className="select select-bordered w-full"
                >
                  {cities.map((city: any) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Launched After Date (optional)</label>
                <input
                  {...registerEditNeighborhood("launchedAfter")}
                  type="date"
                  className="input w-full"
                  placeholder="YYYY-MM-DD"
                />
                <p className="text-xs text-dark-muted mt-1">
                  Neighborhood will be enabled after this event date
                </p>
              </div>
              <div>
                <label className="label">Free Until Date (optional)</label>
                <input
                  {...registerEditNeighborhood("freeUntil")}
                  type="date"
                  className="input w-full"
                  placeholder="YYYY-MM-DD"
                />
                <p className="text-xs text-dark-muted mt-1">
                  Events on or before this date will not require payment
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  {...registerEditNeighborhood("isActive")}
                  type="checkbox"
                  id="editNeighborhoodActive"
                  className="checkbox"
                />
                <label htmlFor="editNeighborhoodActive" className="label mb-0">
                  Active
                </label>
              </div>
              <div className="modal-action">
                <button
                  type="button"
                  onClick={() => {
                    setEditingNeighborhood(null);
                    resetEditNeighborhood();
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateNeighborhood.isPending}
                >
                  {updateNeighborhood.isPending
                    ? "Updating..."
                    : "Update Neighborhood"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit Restaurant Modal */}
      {editingRestaurant && (
        <div
          className="modal modal-open"
          onClick={() => {
            setEditingRestaurant(null);
            resetEditRestaurant();
            setEditRestaurantTags([]);
            setEditGmapsQuery("");
            setEditGmapsResults([]);
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="modal-box max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-xl mb-4 flex-shrink-0">
              Edit Restaurant: {editingRestaurant.name}
            </h3>
            
            {/* Embedded Map - shows current location */}
            <div className="mb-4 flex-shrink-0">
              <label className="label flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4" />
                Location
              </label>
              <div className="rounded-lg overflow-hidden border border-dark-border bg-dark-border/30 h-48">
                <EditRestaurantMap
                  lat={watchEditRestaurant("lat")}
                  lng={watchEditRestaurant("lng")}
                  address={watchEditRestaurant("address")}
                />
              </div>
            </div>
            
            {/* Google Maps Search for Backfilling */}
            <div className="bg-dark-border/30 rounded-lg p-4 mb-4">
              <label className="label flex items-center gap-2">
                <Search className="w-4 h-4" />
                Fetch from Google Maps (overwrites fields with the selected result)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editGmapsQuery}
                  onChange={(e) => setEditGmapsQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleEditGmapsSearch())}
                  className="input flex-1"
                  placeholder="Search Google Maps..."
                />
                <button
                  type="button"
                  onClick={handleEditGmapsSearch}
                  className="btn btn-primary"
                  disabled={googleMapsSearch.isPending}
                >
                  {googleMapsSearch.isPending ? "..." : "Search"}
                </button>
              </div>
              
              {/* Google Maps Search Results */}
              {editGmapsResults.length > 0 && (
                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                  {editGmapsResults.map((place) => (
                    <div
                      key={place.placeId}
                      onClick={() => selectEditGmapsPlace(place)}
                      className="p-3 bg-dark-surface rounded-lg cursor-pointer hover:bg-dark-border/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{place.name}</p>
                          <p className="text-sm text-dark-muted truncate">{place.address}</p>
                          {place.rating && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span className="text-sm">{place.rating}</span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost text-primary-500"
                        >
                          Use
                        </button>
                      </div>
                      {place.tags.length > 0 && (
                        <div className="relative group/tags">
                          <div className="flex flex-wrap gap-1 mt-2">
                            {place.tags.slice(0, 4).map((tag) => (
                              <span
                                key={tag}
                                className="px-1.5 py-0.5 bg-primary-500/10 text-primary-500 text-xs rounded"
                              >
                                {tag.replace(/_/g, " ")}
                              </span>
                            ))}
                            {place.tags.length > 4 && (
                              <span className="text-xs text-dark-muted cursor-help">+{place.tags.length - 4} more</span>
                            )}
                          </div>
                          {place.tags.length > 4 && (
                            <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/tags:block">
                              <div className="bg-dark-surface border border-dark-border rounded-lg p-2 shadow-xl max-w-xs">
                                <div className="flex flex-wrap gap-1">
                                  {place.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="px-1.5 py-0.5 bg-primary-500/10 text-primary-500 text-xs rounded"
                                    >
                                      {tag.replace(/_/g, " ")}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-dark-muted mt-2">
                Selecting a result overwrites address, phone, website, Google Maps link, coordinates, type, and tags.
              </p>
            </div>
            
            <form
              onSubmit={handleSubmitEditRestaurant(onEditRestaurant)}
              className="space-y-4 flex-1 overflow-y-auto min-h-0"
            >
              <div>
                <label className="label">Restaurant Name</label>
                <input
                  {...registerEditRestaurant("name")}
                  type="text"
                  className="input w-full"
                  placeholder="e.g., Blue Hill"
                />
              </div>
              <div>
                <label className="label">Address</label>
                <input
                  {...registerEditRestaurant("address")}
                  type="text"
                  className="input w-full"
                  placeholder="e.g., 75 Washington Place"
                />
              </div>
              <div>
                <label className="label">Neighborhood</label>
                <select
                  {...registerEditRestaurant("neighborhoodId")}
                  className="select select-bordered w-full"
                >
                  {neighborhoods.map((neighborhood: any) => (
                    <option key={neighborhood.id} value={neighborhood.id}>
                      {neighborhood.name} ({neighborhood.cityName})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Type</label>
                  <select
                    {...registerEditRestaurant("type")}
                    className="select select-bordered w-full"
                  >
                    <option value="restaurant">Restaurant</option>
                    <option value="bar">Bar</option>
                    <option value="cafe">Cafe</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Location Coordinates</label>
                  {watchEditRestaurant("address") && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(watchEditRestaurant("address"))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-500 hover:underline"
                    >
                      Pick on Map →
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label text-xs">Latitude (optional)</label>
                    <input
                      {...registerEditRestaurant("lat", { valueAsNumber: true })}
                      type="number"
                      step="any"
                      className="input w-full"
                      placeholder="e.g., 40.7282"
                    />
                  </div>
                  <div>
                    <label className="label text-xs">Longitude (optional)</label>
                    <input
                      {...registerEditRestaurant("lng", { valueAsNumber: true })}
                      type="number"
                      step="any"
                      className="input w-full"
                      placeholder="e.g., -73.9942"
                    />
                  </div>
                </div>
                {watchEditRestaurant("lat") && watchEditRestaurant("lng") && (
                  <div className="mt-2">
                    <a
                      href={`https://www.google.com/maps?q=${watchEditRestaurant("lat")},${watchEditRestaurant("lng")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-500 hover:underline"
                    >
                      View on Google Maps →
                    </a>
                  </div>
                )}
              </div>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="label">Phone (optional)</label>
                   <input
                     {...registerEditRestaurant("phone")}
                     type="text"
                     className="input w-full"
                     placeholder="e.g., (212) 555-1234"
                   />
                 </div>
                 <div>
                   <label className="label">Website (optional)</label>
                   <input
                     {...registerEditRestaurant("website")}
                     type="url"
                     className="input w-full"
                     placeholder="e.g., https://example.com"
                   />
                 </div>
               </div>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="label">Google Maps Link (optional)</label>
                   <input
                     {...registerEditRestaurant("googleMapsLink")}
                     type="url"
                     className="input w-full"
                     placeholder="e.g., https://goo.gl/maps/..."
                   />
                 </div>
                 <div>
                   <label className="label">Booking Link (optional)</label>
                   <input
                     {...registerEditRestaurant("bookingLink")}
                     type="url"
                     className="input w-full"
                     placeholder="e.g., OpenTable, Resy link"
                   />
                 </div>
               </div>
               
               {/* Tags Section */}
               <div>
                 <label className="label flex items-center gap-2">
                   <Tag className="w-4 h-4" />
                   Tags (for matching preferences)
                 </label>
                 <div className="flex flex-wrap gap-2 mb-2">
                   {editRestaurantTags.map((tag) => (
                     <span
                       key={tag}
                       className="px-2 py-1 bg-primary-500/20 text-primary-500 text-sm rounded-full flex items-center gap-1"
                     >
                       {tag.replace(/_/g, " ")}
                       <button
                         type="button"
                         onClick={() => removeEditTag(tag)}
                         className="hover:bg-primary-500/30 rounded-full p-0.5"
                       >
                         <X className="w-3 h-3" />
                       </button>
                     </span>
                   ))}
                   {editRestaurantTags.length === 0 && (
                     <span className="text-sm text-dark-muted">No tags added</span>
                   )}
                 </div>
                 <div className="flex gap-2">
                   <input
                     type="text"
                     value={editNewTag}
                     onChange={(e) => setEditNewTag(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEditTag())}
                     className="input flex-1"
                     placeholder="Add a tag (e.g., italian, has_vegetarian_options)"
                   />
                   <button
                     type="button"
                     onClick={addEditTag}
                     className="btn btn-ghost"
                   >
                     Add
                   </button>
                 </div>
                 <p className="text-xs text-dark-muted mt-1">
                   Common tags: italian, mexican, upscale, budget_friendly, has_vegetarian_options, has_vegan_options, outdoor_seating
                 </p>
               </div>
               
              <div className="flex items-center space-x-2">
                <input
                  {...registerEditRestaurant("isActive")}
                  type="checkbox"
                  id="editRestaurantActive"
                  className="checkbox"
                />
                <label htmlFor="editRestaurantActive" className="label mb-0">
                  Active
                </label>
              </div>
              <div className="modal-action">
                <button
                  type="button"
                  onClick={() => {
                    setEditingRestaurant(null);
                    resetEditRestaurant();
                    setEditRestaurantTags([]);
                    setEditGmapsQuery("");
                    setEditGmapsResults([]);
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateRestaurant.isPending}
                >
                  {updateRestaurant.isPending
                    ? "Updating..."
                    : "Update Restaurant"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Batch Edit Modal */}
      {showBatchEdit && (
        <div
          className="modal modal-open"
          onClick={() => {
            setShowBatchEdit(false);
            setBatchEditResults({});
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="modal-box max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-dark-border">
              <div>
                <h3 className="font-bold text-xl">Batch Update from Google Maps</h3>
                <p className="text-sm text-dark-muted mt-1">
                  Review and accept updates for {Object.keys(batchEditResults).length} restaurants
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const allAccepted = Object.values(batchEditResults).every(r => r.accepted && r.gmapsResult);
                    setBatchEditResults(prev => {
                      const updated = { ...prev };
                      for (const id in updated) {
                        if (updated[id].gmapsResult) {
                          updated[id].accepted = !allAccepted;
                        }
                      }
                      return updated;
                    });
                  }}
                  className="btn btn-sm btn-ghost"
                  disabled={Object.values(batchEditResults).filter(r => r.gmapsResult).length === 0}
                >
                  {Object.values(batchEditResults).every(r => !r.gmapsResult || r.accepted) ? "Deselect All" : "Select All"}
                </button>
                <button
                  onClick={() => {
                    setShowBatchEdit(false);
                    setBatchEditResults({});
                  }}
                  className="p-2 hover:bg-dark-border rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {Object.entries(batchEditResults).map(([id, result]) => (
                <div
                  key={id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    result.accepted
                      ? "bg-green-500/10 border-green-500"
                      : "bg-dark-border/30 border-dark-border"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleBatchAccept(id)}
                      disabled={result.loading || !result.gmapsResult}
                      className={`mt-1 flex-shrink-0 ${
                        result.loading || !result.gmapsResult
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      {result.accepted ? (
                        <CheckSquare className="w-5 h-5 text-green-500" />
                      ) : (
                        <Square className="w-5 h-5 text-dark-muted" />
                      )}
                    </button>

                    {/* Content */}
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      {/* Current Data */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm text-dark-muted">Current</h4>
                        <div className="space-y-1 text-sm">
                          <p className="font-medium">{result.restaurant.name}</p>
                          <p className="text-dark-muted">{result.restaurant.address}</p>
                          {result.restaurant.phone && (
                            <p className="text-dark-muted">📞 {result.restaurant.phone}</p>
                          )}
                          {result.restaurant.website && (
                            <p className="text-dark-muted">🌐 {result.restaurant.website}</p>
                          )}
                          {result.restaurant.type && (
                            <p className="text-dark-muted">Type: {result.restaurant.type}</p>
                          )}
                          {result.restaurant.lat && result.restaurant.lng && (
                            <p className="text-dark-muted">
                              📍 {result.restaurant.lat}, {result.restaurant.lng}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Google Maps Result */}
                      <div>
                        <h4 className="font-semibold mb-2 text-sm text-primary-500">Google Maps</h4>
                        {result.loading ? (
                          <div className="flex items-center gap-2 text-sm text-dark-muted">
                            <Loader size={20} />
                            Searching...
                          </div>
                        ) : result.error ? (
                          <div className="text-sm text-red-500">{result.error}</div>
                        ) : result.gmapsResult ? (
                          <div className="space-y-1 text-sm">
                            <p className="font-medium">{result.gmapsResult.name}</p>
                            <p className="text-dark-muted">{result.gmapsResult.address}</p>
                            {result.gmapsResult.phone && (
                              <p className="text-dark-muted">📞 {result.gmapsResult.phone}</p>
                            )}
                            {result.gmapsResult.website && (
                              <p className="text-dark-muted">🌐 {result.gmapsResult.website}</p>
                            )}
                            {result.gmapsResult.types && (
                              <div>
                                <p className="text-dark-muted mb-1">
                                  Type: {(() => {
                                    const primary = result.gmapsResult!.primaryType;
                                    if (primary === "bar" || primary === "cocktail_bar" || primary === "wine_bar" || primary === "pub" || primary === "night_club") {
                                      return "bar";
                                    }
                                    if (primary === "cafe" || primary === "coffee_shop") {
                                      return "cafe";
                                    }
                                    return "restaurant";
                                  })()}
                                </p>
                              </div>
                            )}
                            {result.gmapsResult.lat && result.gmapsResult.lng && (
                              <p className="text-dark-muted">
                                📍 {result.gmapsResult.lat.toFixed(4)}, {result.gmapsResult.lng.toFixed(4)}
                              </p>
                            )}
                            {result.gmapsResult.rating && (
                              <div className="flex items-center gap-1 mt-1">
                                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                <span className="text-xs">{result.gmapsResult.rating}</span>
                                {result.gmapsResult.userRatingCount && (
                                  <span className="text-xs text-dark-muted">
                                    ({result.gmapsResult.userRatingCount} reviews)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-between pt-4 mt-4 border-t border-dark-border">
              <div className="text-sm text-dark-muted">
                {Object.values(batchEditResults).filter(r => r.accepted).length} of{" "}
                {Object.values(batchEditResults).filter(r => r.gmapsResult).length} selected
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowBatchEdit(false);
                    setBatchEditResults({});
                  }}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={applyBatchUpdates}
                  disabled={
                    Object.values(batchEditResults).filter(r => r.accepted).length === 0 ||
                    updateRestaurant.isPending
                  }
                  className="btn btn-primary"
                >
                  {updateRestaurant.isPending
                    ? "Updating..."
                    : `Apply ${Object.values(batchEditResults).filter(r => r.accepted).length} Updates`}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
